import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Car,
  CalendarClock,
  UserCheck,
  UserPlus,
  Users,
  FilterX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { PhoneNumberInput } from "@/shared/components/PhoneNumberInput";
import { TablePagination } from "@/shared/components/TablePagination";
import {
  useDrivers,
  useCreateDriver,
  useUpdateDriver,
  useDeleteDriver,
  usePromoteDriverToUser,
} from "../hooks/useDrivers";
import { useAllVehicles } from "../hooks/useVehicles";
import type { DriverDto } from "../services/driversApi";
import {
  useCheckDriverAvailability,
  useCreateDriverAvailability,
  useDeactivateDriverAvailability,
  useDriverAvailabilityPaged,
  usePreviewDriverAvailabilityImpacts,
  useUpdateDriverAvailability,
} from "../hooks/useDriverAvailability";
import type {
  DriverAvailabilityConflictDto,
  DriverAvailabilityDto,
  DriverAvailabilityImpactResultDto,
  DriverAvailabilityStatus,
} from "../services/driverAvailabilityApi";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import { DriverAvailabilityCalendarOverview } from "../components/DriverAvailabilityCalendarOverview";
import {
  addDurationHours,
  buildDefaultAvailabilityWindow,
  formatDuration,
  toIsoFromLocalInput,
  toLocalInputValue,
} from "../components/driverAvailabilityTime";

const REASON_REQUIRED_STATUSES: DriverAvailabilityStatus[] = [
  "Unavailable",
  "PartiallyAvailable",
  "OnLeave",
  "Sick",
  "Other",
];

const AVAILABILITY_STATUSES: DriverAvailabilityStatus[] = [
  "Available",
  "Unavailable",
  "PartiallyAvailable",
  "OnLeave",
  "Sick",
  "Assigned",
  "Other",
];

const availabilityStatusLabel = (status: DriverAvailabilityStatus) =>
  status.replace(/([a-z])([A-Z])/g, "$1 $2");

const fmtDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString("en-KE", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "-";

const formatAvailabilityConflictMessage = (conflict: DriverAvailabilityConflictDto): string => {
  const hasWindow = !!conflict.startDateTime && !!conflict.endDateTime;
  const windowText = hasWindow
    ? `${fmtDateTime(conflict.startDateTime)} to ${fmtDateTime(conflict.endDateTime)}`
    : null;

  if (conflict.conflictType === "delivery_plan_assignment" && conflict.deliveryPlanName && windowText) {
    return `Driver is already assigned to plan '${conflict.deliveryPlanName}' from ${windowText}.`;
  }

  if (conflict.conflictType === "no_available_window" && windowText) {
    return `No active 'Available' window fully covers ${windowText}.`;
  }

  if ((conflict.conflictType === "availability_window" || conflict.conflictType === "partial_availability") && conflict.status && windowText) {
    return `Driver has status ${availabilityStatusLabel(conflict.status)} from ${windowText}.`;
  }

  return conflict.message;
};

const getInitials = (value: string) =>
  value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "DR";

const normalizeVehicleReg = (value?: string) =>
  value?.trim().toUpperCase() ?? "";

const statusVariant = (status: DriverAvailabilityStatus): "default" | "secondary" | "destructive" | "outline" => {
  if (status === "Available") return "default";
  if (status === "Unavailable" || status === "Sick") return "destructive";
  if (status === "PartiallyAvailable" || status === "Assigned") return "outline";
  return "secondary";
};

const buildImpactSummaryText = (baseText: string, impacts: DriverAvailabilityImpactResultDto): string => {
  const segments: string[] = [baseText];

  if (impacts.impactedDeliveryPlans.length > 0) {
    const names = impacts.impactedDeliveryPlans
      .slice(0, 3)
      .map((plan) => plan.name)
      .join(", ");
    const suffix = impacts.impactedDeliveryPlans.length > 3 ? ", ..." : "";
    segments.push(
      `Impacts ${impacts.impactedDeliveryPlans.length} active delivery plan(s): ${names}${suffix}.`
    );
  }

  if (impacts.impactedDispatches.length > 0) {
    const numbers = impacts.impactedDispatches
      .slice(0, 3)
      .map((dispatch) => dispatch.dispatchNumber)
      .join(", ");
    const suffix = impacts.impactedDispatches.length > 3 ? ", ..." : "";
    segments.push(
      `Impacts ${impacts.impactedDispatches.length} active dispatch record(s): ${numbers}${suffix}.`
    );
  }

  return segments.join(" ");
};

// ── Create Dialog ─────────────────────────────────────────────────────────

const EMPTY = {
  fullName: "",
  phone: "",
  licenseNumber: "",
  defaultVehicleReg: "",
  notes: "",
};

const CreateDriverDialog = ({ drivers }: { drivers: DriverDto[] }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const createMutation = useCreateDriver();
  const { data: vehicles = [], isLoading: vehiclesLoading } = useAllVehicles();
  const confirm = useConfirm();

  const assignedVehicleOwners = useMemo(
    () => {
      const owners = new Map<string, string>();
      drivers.forEach((driver) => {
        const reg = normalizeVehicleReg(driver.defaultVehicleReg);
        if (reg) {
          owners.set(reg, driver.fullName);
        }
      });
      return owners;
    },
    [drivers]
  );

  const vehicleOptions = useMemo(
    () =>
      [...vehicles]
        .filter((vehicle) => vehicle.isActive)
        .sort((a, b) => a.registrationNumber.localeCompare(b.registrationNumber)),
    [vehicles]
  );

  const unassignedVehicleCount = useMemo(
    () =>
      vehicleOptions.filter(
        (vehicle) => !assignedVehicleOwners.has(normalizeVehicleReg(vehicle.registrationNumber))
      ).length,
    [vehicleOptions, assignedVehicleOwners]
  );

  const set = (field: keyof typeof EMPTY, value: string) =>
    setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    confirm({
      title: "Onboard driver?",
      description: `“${form.fullName}” will be added as a delivery driver.`,
      confirmLabel: "Onboard driver",
      onConfirm: () =>
        withConfirmSuccess(
          createMutation.mutateAsync({
            fullName: form.fullName,
            phone: form.phone,
            licenseNumber: form.licenseNumber || undefined,
            defaultVehicleReg: form.defaultVehicleReg || undefined,
            notes: form.notes || undefined,
          }),
          `${form.fullName} onboarded`
        ).then(() => {
          setForm(EMPTY);
          setOpen(false);
        }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Add Driver
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Onboard New Driver</DialogTitle>
          <DialogDescription>
            Capture driver profile details for dispatch assignment and route execution.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Field>
              <FieldLabel>Full Name *</FieldLabel>
              <Input
                placeholder="e.g. John Kamau"
                value={form.fullName}
                onChange={(e) => set("fullName", e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel>License Number</FieldLabel>
              <Input
                placeholder="e.g. DL-KE-001234"
                value={form.licenseNumber}
                onChange={(e) => set("licenseNumber", e.target.value)}
              />
            </Field>
            <PhoneNumberInput
              label="Phone Number"
              required
              value={form.phone}
              onChange={(v) => set("phone", v)}
            />
            <Field>
              <FieldLabel>Default Vehicle Reg</FieldLabel>
              <Select
                value={form.defaultVehicleReg || "none"}
                onValueChange={(value) => set("defaultVehicleReg", value === "none" ? "" : value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={vehiclesLoading ? "Loading vehicles..." : "Select default vehicle"} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">No default vehicle</SelectItem>
                  {vehicleOptions.map((vehicle) => {
                    const assignedTo = assignedVehicleOwners.get(
                      normalizeVehicleReg(vehicle.registrationNumber)
                    );
                    return (
                      <SelectItem
                        key={vehicle.id}
                        value={vehicle.registrationNumber}
                        disabled={!!assignedTo}
                      >
                        {vehicle.registrationNumber}
                        {vehicle.nameOrDescription ? ` - ${vehicle.nameOrDescription}` : ""}
                        {assignedTo ? ` (Assigned to ${assignedTo})` : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {vehicleOptions.length === 0
                  ? "No active vehicles found."
                  : unassignedVehicleCount === 0
                    ? "All active vehicles are currently assigned. Revoke one first to reassign."
                    : "Assigned vehicles are shown with their current driver and cannot be selected."}
              </p>
            </Field>
          </div>
          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              placeholder="Optional notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={3}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Saving…" : "Onboard Driver"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Availability: Create Dialog ───────────────────────────────────────────

const EMPTY_AVAILABILITY = {
  driverId: "",
  startDateTime: "",
  endDateTime: "",
  status: "Available" as DriverAvailabilityStatus,
  reason: "",
  notes: "",
};

const CreateAvailabilityDialog = ({ drivers }: { drivers: DriverDto[] }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_AVAILABILITY);
  const createMutation = useCreateDriverAvailability();
  const previewImpactMutation = usePreviewDriverAvailabilityImpacts();
  const confirm = useConfirm();
  const activeDrivers = useMemo(
    () => drivers.filter((driver) => driver.isActive),
    [drivers]
  );

  const reasonRequired = REASON_REQUIRED_STATUSES.includes(form.status);
  const isWindowValid =
    !!form.startDateTime &&
    !!form.endDateTime &&
    new Date(form.startDateTime).getTime() < new Date(form.endDateTime).getTime();
  const hasWindowInput = !!form.startDateTime && !!form.endDateTime;
  const computedDuration =
    hasWindowInput && isWindowValid
      ? formatDuration(form.startDateTime, form.endDateTime)
      : "-";

  const selectedDriver = useMemo(
    () => drivers.find((driver) => driver.id === form.driverId),
    [drivers, form.driverId]
  );

  useEffect(() => {
    if (!open) return;
    if (form.startDateTime && form.endDateTime) return;

    const defaults = buildDefaultAvailabilityWindow();
    setForm((prev) => ({
      ...prev,
      startDateTime: prev.startDateTime || defaults.start,
      endDateTime: prev.endDateTime || defaults.end,
    }));
  }, [open, form.startDateTime, form.endDateTime]);

  const set = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [field]: value }));

  const applyDurationHours = (hours: number) => {
    if (!form.startDateTime) return;
    set("endDateTime", addDurationHours(form.startDateTime, hours));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.driverId || !isWindowValid) return;

    const payload = {
      driverId: form.driverId,
      startDateTime: toIsoFromLocalInput(form.startDateTime),
      endDateTime: toIsoFromLocalInput(form.endDateTime),
      status: form.status,
      reason: form.reason || undefined,
      notes: form.notes || undefined,
    };

    let impactDescription = selectedDriver
      ? `This window will be saved for ${selectedDriver.fullName} and used by dispatch planning checks.`
      : "This availability window will be used by dispatch planning checks.";

    let requiresOverride = false;

    if (form.status !== "Available") {
      const impacts = await previewImpactMutation.mutateAsync({
        driverId: payload.driverId,
        startDateTime: payload.startDateTime,
        endDateTime: payload.endDateTime,
        status: payload.status,
      });

      requiresOverride = impacts.requiresManagerOverride;
      impactDescription = buildImpactSummaryText(impactDescription, impacts);
    }

    confirm({
      title: "Save availability window?",
      description: impactDescription,
      confirmLabel: "Save window",
      onConfirm: () =>
        withConfirmSuccess(
          createMutation.mutateAsync({
            ...payload,
            overrideAssignmentConflicts: requiresOverride,
            overrideReason: requiresOverride
              ? form.reason || form.notes || "Dispatch impact override confirmed"
              : undefined,
          }),
          "Availability window created"
        ).then(() => {
          setForm(EMPTY_AVAILABILITY);
          setOpen(false);
        }),
    });
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setForm(EMPTY_AVAILABILITY);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Add Availability
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="shrink-0 border-b px-5 py-4 sm:px-6">
          <DialogTitle>Create Driver Availability Window</DialogTitle>
          <DialogDescription>
            Define a precise availability window used by dispatch checks and trip assignment planning.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 sm:px-6">
            <section>
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-foreground">Assignment Details</h4>
                <p className="text-xs text-muted-foreground">
                  Select the driver and the availability status for this window.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Field>
                  <FieldLabel>Driver *</FieldLabel>
                  <Select value={form.driverId} onValueChange={(v) => set("driverId", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select active driver" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeDrivers.map((driver) => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.fullName} ({driver.phone})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field>
                  <FieldLabel>Status *</FieldLabel>
                  <Select
                    value={form.status}
                    onValueChange={(v) => set("status", v as DriverAvailabilityStatus)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {AVAILABILITY_STATUSES.map((status) => (
                        <SelectItem key={status} value={status}>
                          {availabilityStatusLabel(status)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </section>

            <section className="border-t pt-4">
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-foreground">Time Window</h4>
                <p className="text-xs text-muted-foreground">
                  Time is captured in your local timezone and stored as UTC.
                </p>
              </div>

              <div className="space-y-3">
                <Field>
                  <FieldLabel>Start *</FieldLabel>
                  <DateTimePicker
                    id="availability-start"
                    value={form.startDateTime}
                    onChange={(value) => set("startDateTime", value)}
                    required
                  />
                </Field>
                <Field>
                  <FieldLabel>End *</FieldLabel>
                  <DateTimePicker
                    id="availability-end"
                    value={form.endDateTime}
                    onChange={(value) => set("endDateTime", value)}
                    min={form.startDateTime || undefined}
                    required
                  />
                </Field>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Quick duration</span>
                <Button type="button" variant="outline" size="sm" onClick={() => applyDurationHours(4)}>
                  4h
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => applyDurationHours(8)}>
                  8h
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => applyDurationHours(24)}>
                  1 day
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => applyDurationHours(24 * 7)}>
                  7 days
                </Button>
                <span className="ml-auto text-xs text-muted-foreground">
                  Duration: {computedDuration}
                </span>
              </div>

              {!isWindowValid && hasWindowInput && (
                <p className="mt-2 text-sm text-destructive" role="alert">
                  End time must be later than start time.
                </p>
              )}
            </section>

            <section className="border-t pt-4">
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-foreground">Operational Context</h4>
                <p className="text-xs text-muted-foreground">
                  Add context to help dispatch admins understand scheduling decisions.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field>
                  <FieldLabel>
                    Reason {reasonRequired ? "*" : "(optional)"}
                  </FieldLabel>
                  <Textarea
                    value={form.reason}
                    onChange={(e) => set("reason", e.target.value)}
                    placeholder={
                      reasonRequired
                        ? "Required for this status"
                        : "Why is this window needed?"
                    }
                    required={reasonRequired}
                    rows={2}
                    className="min-h-16 resize-y"
                  />
                  {reasonRequired && (
                    <p className="mt-1 text-xs text-amber-700">
                      Required for the selected status.
                    </p>
                  )}
                </Field>

                <Field>
                  <FieldLabel>Notes (optional)</FieldLabel>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    placeholder="Return timing or dispatch context"
                    rows={2}
                    className="min-h-16 resize-y"
                  />
                </Field>
              </div>
            </section>

            {activeDrivers.length === 0 && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Add and activate at least one driver before creating availability windows.
              </p>
            )}
          </div>

          <DialogFooter className="shrink-0 border-t bg-background px-5 py-3 sm:px-6">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                createMutation.isPending
                || previewImpactMutation.isPending
                || !isWindowValid
                || !form.driverId
                || activeDrivers.length === 0
              }
            >
              {createMutation.isPending || previewImpactMutation.isPending ? "Saving..." : "Save Availability"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Availability: Edit Dialog ─────────────────────────────────────────────

const EditAvailabilityDialog = ({
  availability,
  open,
  onOpenChange,
}: {
  availability: DriverAvailabilityDto;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}) => {
  const [form, setForm] = useState({
    startDateTime: toLocalInputValue(availability.startDateTime),
    endDateTime: toLocalInputValue(availability.endDateTime),
    status: availability.status,
    reason: availability.reason ?? "",
    notes: availability.notes ?? "",
    isActive: availability.isActive,
  });

  const updateMutation = useUpdateDriverAvailability();
  const previewImpactMutation = usePreviewDriverAvailabilityImpacts();
  const confirm = useConfirm();
  const reasonRequired = REASON_REQUIRED_STATUSES.includes(form.status);
  const isWindowValid =
    !!form.startDateTime &&
    !!form.endDateTime &&
    new Date(form.startDateTime).getTime() < new Date(form.endDateTime).getTime();

  const set = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isWindowValid) return;

    const payload = {
      startDateTime: toIsoFromLocalInput(form.startDateTime),
      endDateTime: toIsoFromLocalInput(form.endDateTime),
      status: form.status,
      reason: form.reason || undefined,
      notes: form.notes || undefined,
      isActive: form.isActive,
    };

    let description = "Driver availability will be updated.";
    let requiresOverride = false;

    if (form.status !== "Available") {
      const impacts = await previewImpactMutation.mutateAsync({
        driverId: availability.driverId,
        startDateTime: payload.startDateTime,
        endDateTime: payload.endDateTime,
        status: payload.status,
        excludeAvailabilityId: availability.id,
      });

      requiresOverride = impacts.requiresManagerOverride;
      description = buildImpactSummaryText("Driver availability will be updated.", impacts);
    }

    confirm({
      title: "Save availability changes?",
      description,
      confirmLabel: "Save changes",
      onConfirm: () =>
        withConfirmSuccess(
          updateMutation.mutateAsync({
            id: availability.id,
            data: {
              ...payload,
              overrideAssignmentConflicts: requiresOverride,
              overrideReason: requiresOverride
                ? form.reason || form.notes || "Dispatch impact override confirmed"
                : undefined,
            },
          }),
          "Availability updated"
        ).then(() => onOpenChange(false)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit Availability ({availability.driverName})</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid gap-4 md:grid-cols-2">
            <Field>
              <FieldLabel>Start *</FieldLabel>
              <DateTimePicker
                id={`availability-edit-start-${availability.id}`}
                value={form.startDateTime}
                onChange={(value) => set("startDateTime", value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel>End *</FieldLabel>
              <DateTimePicker
                id={`availability-edit-end-${availability.id}`}
                value={form.endDateTime}
                onChange={(value) => set("endDateTime", value)}
                min={form.startDateTime || undefined}
                required
              />
            </Field>
          </div>

          {!isWindowValid && form.startDateTime && form.endDateTime && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              End time must be later than start time.
            </p>
          )}

          <Field>
            <FieldLabel>Status *</FieldLabel>
            <Select
              value={form.status}
              onValueChange={(v) => set("status", v as DriverAvailabilityStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AVAILABILITY_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {availabilityStatusLabel(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>
              Reason {reasonRequired ? "*" : ""}
            </FieldLabel>
            <Textarea
              value={form.reason}
              onChange={(e) => set("reason", e.target.value)}
              placeholder={reasonRequired ? "Provide reason for this status" : "Optional reason"}
              required={reasonRequired}
              rows={2}
            />
          </Field>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Optional notes"
              rows={3}
            />
          </Field>

          <div className="flex items-center gap-3">
            <Checkbox
              id={`availability-active-${availability.id}`}
              checked={form.isActive}
              onCheckedChange={(v) => set("isActive", v === true)}
            />
            <Label htmlFor={`availability-active-${availability.id}`}>Active</Label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending || previewImpactMutation.isPending || !isWindowValid}
            >
              {updateMutation.isPending || previewImpactMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Availability: Check Panel ─────────────────────────────────────────────

const DriverAvailabilityCheckPanel = ({ drivers }: { drivers: DriverDto[] }) => {
  const checkMutation = useCheckDriverAvailability();
  const [driverId, setDriverId] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const isWindowValid =
    !!startDateTime &&
    !!endDateTime &&
    new Date(startDateTime).getTime() < new Date(endDateTime).getTime();

  useEffect(() => {
    if (startDateTime && endDateTime) return;
    const defaults = buildDefaultAvailabilityWindow();
    setStartDateTime((prev) => prev || defaults.start);
    setEndDateTime((prev) => prev || defaults.end);
  }, [startDateTime, endDateTime]);

  const onCheck = (e: React.FormEvent) => {
    e.preventDefault();
    if (!driverId || !isWindowValid) return;
    checkMutation.mutate({
      driverId,
      startDateTime: toIsoFromLocalInput(startDateTime),
      endDateTime: toIsoFromLocalInput(endDateTime),
    });
  };

  const result = checkMutation.data;
  const hasCoverageConflict =
    result?.conflicts.some((conflict) => conflict.conflictType === "no_available_window") ?? false;
  const hasAssignedPlanConflict =
    result?.conflicts.some((conflict) => conflict.conflictType === "delivery_plan_assignment") ?? false;

  return (
    <Card className="py-0">
      <CardHeader className="border-b py-4">
        <CardTitle className="text-base">Quick Availability Check</CardTitle>
        <CardDescription>
          Validate if a driver can be assigned to a plan for a specific date and time window. Availability windows
          do not override overlapping delivery plan assignments.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 py-4">
        <form onSubmit={onCheck} className="grid gap-3 lg:grid-cols-4">
          <Select value={driverId} onValueChange={setDriverId}>
            <SelectTrigger>
              <SelectValue placeholder="Select driver" />
            </SelectTrigger>
            <SelectContent>
              {drivers.filter((d) => d.isActive).map((driver) => (
                <SelectItem key={driver.id} value={driver.id}>
                  {driver.fullName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DateTimePicker
            id="availability-check-start"
            value={startDateTime}
            onChange={setStartDateTime}
            required
          />
          <DateTimePicker
            id="availability-check-end"
            value={endDateTime}
            onChange={setEndDateTime}
            min={startDateTime || undefined}
            required
          />
          <Button type="submit" disabled={checkMutation.isPending || !driverId || !isWindowValid}>
            {checkMutation.isPending ? "Checking..." : "Run Check"}
          </Button>
        </form>

        {!isWindowValid && startDateTime && endDateTime && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            End time must be later than start time.
          </p>
        )}

        {result && (
          <div className={`rounded-md border p-3 ${result.isAvailable ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`}>
            <div className="flex items-center justify-between gap-3">
              <p className={`text-sm font-medium ${result.isAvailable ? "text-green-700" : "text-amber-700"}`}>
                {result.isAvailable
                  ? "Driver is available in this time window."
                  : hasAssignedPlanConflict
                    ? "Driver is already assigned to another delivery plan in this time window."
                  : hasCoverageConflict
                    ? "Driver is not available because no active Available window fully covers this period."
                    : "Driver has conflicts in this time window."}
              </p>
              <Badge variant={result.isAvailable ? "default" : "outline"}>
                {result.conflicts.length} conflict{result.conflicts.length === 1 ? "" : "s"}
              </Badge>
            </div>
            {result.matchingWindow && (
              <p className={`mt-1 text-xs ${result.isAvailable ? "text-green-700" : "text-amber-700"}`}>
                Effective segment: {availabilityStatusLabel(result.matchingWindow.status)} ({fmtDateTime(result.matchingWindow.startDateTime)} to {fmtDateTime(result.matchingWindow.endDateTime)})
              </p>
            )}
            <p className={`mt-1 text-xs ${result.isAvailable ? "text-green-700" : "text-amber-700"}`}>
              Matched availability windows: {result.availabilityWindows.length}
            </p>
            {!result.isAvailable && (
              <ul className="mt-2 space-y-1 text-sm text-amber-800">
                {result.conflicts.map((conflict, idx) => (
                  <li key={`${conflict.conflictType}-${idx}`}>- {formatAvailabilityConflictMessage(conflict)}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Availability: Table ───────────────────────────────────────────────────

const DriverAvailabilityTable = ({ drivers }: { drivers: DriverDto[] }) => {
  const confirm = useConfirm();
  const deactivateMutation = useDeactivateDriverAvailability();
  const [search, setSearch] = useState("");
  const [driverId, setDriverId] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [activeOnly, setActiveOnly] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editTarget, setEditTarget] = useState<DriverAvailabilityDto | null>(null);

  const query = useMemo(
    () => ({
      search: search || undefined,
      driverId: driverId === "all" ? undefined : driverId,
      status: status === "all" ? undefined : (status as DriverAvailabilityStatus),
      activeOnly,
      pageNumber,
      pageSize,
    }),
    [search, driverId, status, activeOnly, pageNumber, pageSize]
  );

  const { data, isLoading, isFetching, error } = useDriverAvailabilityPaged(query);

  const rows = data?.items ?? [];
  const hasActiveFilters =
    search.trim().length > 0 || driverId !== "all" || status !== "all" || !activeOnly;

  const handleDeactivate = (row: DriverAvailabilityDto) => {
    confirm({
      title: "Deactivate availability window?",
      description: "This record will no longer be used in active planning checks.",
      variant: "destructive",
      confirmLabel: "Deactivate",
      onConfirm: () =>
        withConfirmSuccess(
          deactivateMutation.mutateAsync({ id: row.id }),
          "Availability deactivated"
        ),
    });
  };

  const clearFilters = () => {
    setSearch("");
    setDriverId("all");
    setStatus("all");
    setActiveOnly(true);
    setPageNumber(1);
  };

  return (
    <Card className="py-0">
      <CardHeader className="border-b py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Availability Registry</CardTitle>
            <CardDescription>
              Browse and manage availability windows used by dispatch planning.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                <FilterX className="mr-2 h-4 w-4" />
                Clear filters
              </Button>
            )}
            <Badge variant="outline">{data?.totalCount ?? 0} records</Badge>
          </div>
        </div>

        <div className="grid gap-3 pt-3 md:grid-cols-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by driver/reason"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPageNumber(1);
              }}
              className="pl-10"
            />
          </div>

          <Select
            value={driverId}
            onValueChange={(value) => {
              setDriverId(value);
              setPageNumber(1);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All drivers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All drivers</SelectItem>
              {drivers.map((driver) => (
                <SelectItem key={driver.id} value={driver.id}>
                  {driver.fullName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={status}
            onValueChange={(value) => {
              setStatus(value);
              setPageNumber(1);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {AVAILABILITY_STATUSES.map((item) => (
                <SelectItem key={item} value={item}>
                  {availabilityStatusLabel(item)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2 rounded-md border px-3">
            <Checkbox
              id="availability-active-only"
              checked={activeOnly}
              onCheckedChange={(value) => {
                setActiveOnly(value === true);
                setPageNumber(1);
              }}
            />
            <Label htmlFor="availability-active-only">Active only</Label>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 py-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Driver</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Window</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-gray-500">
                  Loading availability windows...
                </TableCell>
              </TableRow>
            )}

            {error && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-red-600">
                  Failed to load driver availability.
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-gray-500">
                  No availability records found for current filters.
                </TableCell>
              </TableRow>
            )}

            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-[10px]">{getInitials(row.driverName)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{row.driverName}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant(row.status)}>{availabilityStatusLabel(row.status)}</Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div>{fmtDateTime(row.startDateTime)}</div>
                    <div className="text-gray-500">to {fmtDateTime(row.endDateTime)}</div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDuration(row.startDateTime, row.endDateTime)}
                </TableCell>
                <TableCell className="max-w-[260px] truncate">{row.reason || "-"}</TableCell>
                <TableCell>
                  <Badge variant={row.isActive ? "default" : "secondary"}>
                    {row.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditTarget(row)}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {row.isActive && (
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDeactivate(row)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Deactivate
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {(data?.totalCount ?? 0) > 0 && (
          <TablePagination
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalCount={data?.totalCount ?? 0}
            totalPages={data?.totalPages ?? 0}
            onPageChange={setPageNumber}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNumber(1);
            }}
            isFetching={isFetching}
          />
        )}
      </CardContent>

      {editTarget && (
        <EditAvailabilityDialog
          availability={editTarget}
          open={!!editTarget}
          onOpenChange={(value) => {
            if (!value) setEditTarget(null);
          }}
        />
      )}
    </Card>
  );
};

// ── Edit Dialog ───────────────────────────────────────────────────────────

const EditDriverDialog = ({
  driver,
  drivers,
  open,
  onOpenChange,
}: {
  driver: DriverDto;
  drivers: DriverDto[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const [form, setForm] = useState({
    fullName: driver.fullName,
    phone: driver.phone,
    licenseNumber: driver.licenseNumber ?? "",
    defaultVehicleReg: driver.defaultVehicleReg ?? "",
    notes: driver.notes ?? "",
    isActive: driver.isActive,
  });
  const updateMutation = useUpdateDriver();
  const { data: vehicles = [], isLoading: vehiclesLoading } = useAllVehicles();
  const confirm = useConfirm();

  const assignedVehicleOwners = useMemo(
    () => {
      const owners = new Map<string, string>();
      drivers
        .filter((existingDriver) => existingDriver.id !== driver.id)
        .forEach((existingDriver) => {
          const reg = normalizeVehicleReg(existingDriver.defaultVehicleReg);
          if (reg) {
            owners.set(reg, existingDriver.fullName);
          }
        });
      return owners;
    },
    [drivers, driver.id]
  );

  const vehicleOptions = useMemo(
    () =>
      [...vehicles]
        .sort((a, b) => a.registrationNumber.localeCompare(b.registrationNumber)),
    [vehicles]
  );

  const set = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    confirm({
      title: "Save driver changes?",
      description: `Changes to “${form.fullName}” will be saved.`,
      confirmLabel: "Save changes",
      onConfirm: () =>
        withConfirmSuccess(
          updateMutation.mutateAsync({
            id: driver.id,
            data: {
              fullName: form.fullName,
              phone: form.phone,
              licenseNumber: form.licenseNumber || undefined,
              defaultVehicleReg: form.defaultVehicleReg || undefined,
              notes: form.notes || undefined,
              isActive: form.isActive,
            },
          }),
          "Driver updated"
        ).then(() => onOpenChange(false)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit Driver</DialogTitle>
          <DialogDescription>
            Update profile details, vehicle assignment hint, and active status.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Field>
              <FieldLabel>Full Name *</FieldLabel>
              <Input
                value={form.fullName}
                onChange={(e) => set("fullName", e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel>License Number</FieldLabel>
              <Input
                value={form.licenseNumber}
                onChange={(e) => set("licenseNumber", e.target.value)}
              />
            </Field>
            <PhoneNumberInput
              label="Phone Number"
              required
              value={form.phone}
              onChange={(v) => set("phone", v)}
            />
            <Field>
              <FieldLabel>Default Vehicle Reg</FieldLabel>
              <Select
                value={form.defaultVehicleReg || "none"}
                onValueChange={(value) => set("defaultVehicleReg", value === "none" ? "" : value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={vehiclesLoading ? "Loading vehicles..." : "Select default vehicle"} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">No default vehicle</SelectItem>
                  {vehicleOptions.map((vehicle) => {
                    const assignedTo = assignedVehicleOwners.get(
                      normalizeVehicleReg(vehicle.registrationNumber)
                    );
                    return (
                      <SelectItem
                        key={vehicle.id}
                        value={vehicle.registrationNumber}
                        disabled={!!assignedTo}
                      >
                        {vehicle.registrationNumber}
                        {vehicle.nameOrDescription ? ` - ${vehicle.nameOrDescription}` : ""}
                        {assignedTo ? ` (Assigned to ${assignedTo})` : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Assigned vehicles are shown with their current driver and cannot be selected.
              </p>
            </Field>
          </div>
          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={3}
            />
          </Field>
          <div className="flex items-center gap-3">
            <Checkbox
              id="isActive"
              checked={form.isActive}
              onCheckedChange={(v) => set("isActive", v === true)}
            />
            <Label htmlFor="isActive">Active</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Promote to User Dialog ─────────────────────────────────────────────────

const PromoteToUserDialog = ({
  driver,
  open,
  onOpenChange,
}: {
  driver: DriverDto;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const promote = usePromoteDriverToUser();
  const [email, setEmail] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    await promote.mutateAsync({ id: driver.id, email: email.trim() });
    onOpenChange(false);
    setEmail("");
  };

  return (
    <>
      {promote.isPending && (
        <LoadingAnimation fullScreen className="z-[100]" text="Creating driver account and sending login details…" />
      )}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Promote to System User</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          This will create a login account for <strong>{driver.fullName}</strong> with the
          Driver role. Their phone number (<strong>{driver.phone}</strong>) will be used as
          the username. They can then log into the mobile app.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div className="space-y-1">
            <Label htmlFor="promote-email">Email address *</Label>
            <Input
              id="promote-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="driver@example.com"
              required
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={promote.isPending || !email.trim()}>
              {promote.isPending ? "Creating account…" : "Create Account"}
            </Button>
          </DialogFooter>
        </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ── Drivers Table ─────────────────────────────────────────────────────────

const DriversTable = ({ drivers }: { drivers: DriverDto[] }) => {
  const deleteMutation = useDeleteDriver();
  const confirm = useConfirm();
  const [editTarget, setEditTarget] = useState<DriverDto | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<DriverDto | null>(null);
  const [search, setSearch] = useState("");

  const filtered = drivers.filter(
    (d) =>
      d.fullName.toLowerCase().includes(search.toLowerCase()) ||
      d.phone.includes(search) ||
      (d.defaultVehicleReg ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = drivers.filter((driver) => driver.isActive).length;
  const inactiveCount = drivers.length - activeCount;

  const handleDelete = (id: string, name: string) => {
    confirm({
      title: "Remove driver?",
      description: `“${name}” will be removed from dispatch. This cannot be undone.`,
      variant: "destructive",
      confirmLabel: "Remove",
      onConfirm: () =>
        withConfirmSuccess(deleteMutation.mutateAsync(id), `${name} removed`),
    });
  };

  return (
    <Card className="py-0">
      <CardHeader className="border-b py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Driver Directory</CardTitle>
            <CardDescription>
              Drivers available for dispatch assignment and route execution.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search drivers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            {search && (
              <Button variant="outline" size="sm" onClick={() => setSearch("")}>Clear</Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Badge variant="outline">Total {drivers.length}</Badge>
          <Badge variant="outline">Active {activeCount}</Badge>
          <Badge variant="outline">Inactive {inactiveCount}</Badge>
        </div>
      </CardHeader>

      <CardContent className="py-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>License No.</TableHead>
              <TableHead>Vehicle Reg</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>App Account</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-gray-500">
                  {search ? "No drivers match your search." : "No drivers have been onboarded yet."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((driver) => (
              <TableRow key={driver.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-[10px]">{getInitials(driver.fullName)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium leading-tight">{driver.fullName}</p>
                      {driver.notes && (
                        <p className="text-xs text-muted-foreground truncate max-w-[260px]">{driver.notes}</p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm">{driver.phone}</TableCell>
                <TableCell>{driver.licenseNumber ?? "—"}</TableCell>
                <TableCell>{driver.defaultVehicleReg ?? "—"}</TableCell>
                <TableCell>
                  <Badge variant={driver.isActive ? "default" : "secondary"}>
                    {driver.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell>
                  {!driver.mobileAccountLinked ? (
                    <Badge variant="secondary">Mobile account missing</Badge>
                  ) : !driver.mobileAccountActive ? (
                    <Badge variant="destructive">Account inactive</Badge>
                  ) : (
                    <Badge variant="outline" className="text-green-700 border-green-300">
                      {driver.linkedUserName ?? "Ready for mobile"}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditTarget(driver)}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {!driver.userId && (
                        <DropdownMenuItem onClick={() => setPromoteTarget(driver)}>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Promote to System User
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDelete(driver.id, driver.fullName)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {editTarget && (
        <EditDriverDialog
          driver={editTarget}
          drivers={drivers}
          open={!!editTarget}
          onOpenChange={(v) => { if (!v) setEditTarget(null); }}
        />
      )}
      {promoteTarget && (
        <PromoteToUserDialog
          driver={promoteTarget}
          open={!!promoteTarget}
          onOpenChange={(v) => { if (!v) setPromoteTarget(null); }}
        />
      )}
    </Card>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────

export const DriversManagementPage = () => {
  const { data: drivers, isLoading, error } = useDrivers(false);
  const [activeTab, setActiveTab] = useState("drivers");
  const totalDrivers = drivers?.length ?? 0;
  const activeDrivers = drivers?.filter((driver) => driver.isActive).length ?? 0;
  const inactiveDrivers = totalDrivers - activeDrivers;
  const withDefaultVehicle = drivers?.filter((driver) => !!driver.defaultVehicleReg).length ?? 0;

  if (isLoading) return <LoadingAnimation fullScreen />;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load drivers.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Car className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">Drivers</h1>
          </div>
          <p className="text-gray-500 mt-1">
            Manage dispatch drivers and their planning availability windows.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Drivers</CardDescription>
            <CardTitle className="text-2xl">{totalDrivers}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active Drivers</CardDescription>
            <CardTitle className="text-2xl text-emerald-700">{activeDrivers}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Inactive Drivers</CardDescription>
            <CardTitle className="text-2xl text-amber-700">{inactiveDrivers}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>With Default Vehicle</CardDescription>
            <CardTitle className="text-2xl">{withDefaultVehicle}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="drivers" className="gap-2">
            <Car className="w-4 h-4" /> Drivers ({totalDrivers})
          </TabsTrigger>
          <TabsTrigger value="availability" className="gap-2">
            <CalendarClock className="w-4 h-4" /> Availability
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2">
            <CalendarClock className="w-4 h-4" /> Calendar Overview
          </TabsTrigger>
        </TabsList>

        <TabsContent value="drivers" className="space-y-4 pt-2">
          <div className="flex justify-end">
            <CreateDriverDialog drivers={drivers ?? []} />
          </div>
          <DriversTable drivers={drivers ?? []} />
        </TabsContent>

        <TabsContent value="availability" className="space-y-4 pt-2">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              Keep dispatch assignment checks aligned with real driver schedules.
            </div>
            <CreateAvailabilityDialog drivers={drivers ?? []} />
          </div>

          <DriverAvailabilityCheckPanel drivers={drivers ?? []} />
          <DriverAvailabilityTable drivers={drivers ?? []} />
        </TabsContent>

        <TabsContent value="calendar" className="space-y-4 pt-2">
          <DriverAvailabilityCalendarOverview isActive={activeTab === "calendar"} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
